import { useEffect, useState } from 'react'
import { api, type MappingSuggestion, type SchemeValues } from '../api'
import { subjectText } from '../limits'

/**
 * Saying what each workbook row means — TECHNICAL_DESIGN.md §7.1, §8.3.
 *
 * **This was a JSON textarea with an example in it**, which asked an operator to
 * hand-write a structure whose keys had to match their own row labels exactly,
 * and whose values had to be measure codes they could not see from that screen.
 * The example was not their workbook, so the commonest outcome was importing it
 * unchanged and getting a file of narrative — correct, and not what anybody
 * wanted.
 *
 * The labels come from the sheet and the terms come from the dictionary, so both
 * halves of the mapping are chosen rather than typed. A row left unmapped still
 * imports, as narrative: §7.1 is explicit that nothing may be dropped for want of
 * a mapping, and text this system cannot check is still a limit the mandate
 * carries.
 *
 * **Which holdings a row covers is said here, once** (decision 33). A workbook has
 * a row per limit and nothing in the row saying what it limits, so *Single
 * Corporate Issuer % — 3%* has to be told it means each issuer among corporates.
 * Said at import, the limit is complete when it reaches an approver and when it
 * leaves for an OMS; left to the whole fund, it measures the fund against itself.
 * Rating rows are said here too: a floor under every holding, a floor under the
 * fund's average, and the two policies a floor is read through.
 *
 * The JSON is still shown, and still editable, because a firm with fifty of
 * these will want to paste one they have already built.
 */
export interface Spec {
  measure: string
  basis: string
  /** Which holdings the row's limit covers. Absent on a mapping saved before it
   *  could say, which is the whole fund. */
  subject?: Record<string, unknown>
  /** The rows whose cell is a rating or a policy rather than a number. */
  template?: 'rating_floor' | 'average_rating_floor' | 'evaluation_policy'
  /** A row that is a fact about the fund, such as its benchmark, not a limit. */
  attribute?: string
}

/** Rows that are facts about the fund (decision 34): recorded with it, not proposed. */
const FACT_KINDS: { value: string; label: string; attribute: string }[] = [
  { value: 'fact:benchmark', label: 'The benchmark', attribute: 'benchmark' },
  { value: 'fact:base_currency', label: 'The base currency', attribute: 'base_currency' },
  { value: 'fact:hedging', label: 'Whether the fund is hedged', attribute: 'hedging' },
  { value: 'fact:other', label: 'Another fact about the fund', attribute: '' },
]

function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'fact'
}

const WHOLE_FUND: Record<string, unknown> = { selector: 'portfolio', value: '*' }

/** The choices beside the measures: rows read as ratings, and how ratings are read. */
const RATING_KINDS: { value: string; label: string; rating: boolean; spec: Pick<Spec, 'template' | 'subject'> }[] = [
  {
    value: 'template:rating_floor',
    label: 'Minimum rating, every holding',
    rating: true,
    spec: { template: 'rating_floor', subject: WHOLE_FUND },
  },
  {
    value: 'template:average_rating_floor',
    label: 'Minimum average rating, the fund',
    rating: true,
    spec: { template: 'average_rating_floor', subject: WHOLE_FUND },
  },
  {
    value: 'policy:rating_input',
    label: 'How split ratings are read',
    rating: false,
    spec: { template: 'evaluation_policy', subject: { selector: 'rating_input' } },
  },
  {
    value: 'policy:unrated',
    label: 'How unrated holdings are treated',
    rating: false,
    spec: { template: 'evaluation_policy', subject: { selector: 'unrated' } },
  },
  {
    value: 'policy:downgrade_grace',
    label: 'How long a downgraded holding may be kept',
    rating: false,
    spec: { template: 'evaluation_policy', subject: { selector: 'downgrade_grace' } },
  },
]

function kindOf(spec: Spec | undefined): string {
  if (!spec) return ''
  if (spec.attribute) {
    return FACT_KINDS.some((k) => k.attribute === spec.attribute) ? `fact:${spec.attribute}` : 'fact:other'
  }
  if (spec.template === 'evaluation_policy') return `policy:${String(spec.subject?.selector ?? '')}`
  if (spec.template) return `template:${spec.template}`
  return spec.measure
}

/** One subject's identity, so a saved subject finds its option. */
function subjectKey(subject: Record<string, unknown> | undefined): string {
  const s = subject ?? WHOLE_FUND
  const value = s.value === undefined || s.value === null || s.value === '*' ? '*' : String(s.value)
  return `${String(s.selector ?? 'portfolio')}|${typeof s.scheme === 'string' ? s.scheme : ''}|${value}`
}

interface SubjectOption {
  key: string
  label: string
  subject: Record<string, unknown>
}

/** A classification with more values than this is offered as *each* value, not value by
 *  value: 249 countries twice over is a list nobody reads. A subject naming one still
 *  shows, as itself. */
const LISTED_VALUES = 20

/** Everything a limit can be said to cover, from the Dictionary's own classifications. */
export function subjectOptions(schemes: SchemeValues[]): { group: string; options: SubjectOption[] }[] {
  const option = (label: string, subject: Record<string, unknown>): SubjectOption => ({
    key: subjectKey(subject),
    label,
    subject,
  })
  return [
    { group: '', options: [option('The whole fund', WHOLE_FUND)] },
    {
      group: 'Holdings taken together',
      options: schemes
        .filter((s) => s.members.length <= LISTED_VALUES)
        .flatMap((s) =>
          s.members.map((m) => option(`${m.name}, taken together`, { selector: 'portfolio', scheme: s.code, value: m.code })),
        ),
    },
    {
      group: 'Each issuer',
      options: [
        option('Each issuer', { selector: 'issuer' }),
        ...schemes
          .filter((s) => s.members.length <= LISTED_VALUES)
          .flatMap((s) =>
            s.members.map((m) => option(`Each issuer among ${m.name}`, { selector: 'issuer', scheme: s.code, value: m.code })),
          ),
      ],
    },
    {
      group: 'Each value of a classification',
      options: schemes.map((s) =>
        option(`Each ${s.name.toLowerCase()}`, { selector: s.code, scheme: s.code, value: '*' }),
      ),
    },
  ].filter((g) => g.options.length > 0)
}

export function RowMapping({
  labels,
  value,
  onChange,
}: {
  /** The first column of the sheet — every row the workbook holds. */
  labels: string[]
  value: Record<string, Spec>
  onChange: (next: Record<string, Spec>) => void
}) {
  const [measures, setMeasures] = useState<string[]>([])
  const [bases, setBases] = useState<string[]>([])
  /** Each measure's unit, which decides what it is measured against by default. */
  const [units, setUnits] = useState<Record<string, string>>({})
  const [schemes, setSchemes] = useState<SchemeValues[]>([])
  const [needsSubject, setNeedsSubject] = useState<string[]>([])
  const [raw, setRaw] = useState(false)
  const [text, setText] = useState(() => JSON.stringify(value, null, 2))
  const [error, setError] = useState<string | null>(null)
  const [why, setWhy] = useState<Record<string, string>>({})
  /** What each row's name reads as covering, kept for when its measure is changed. */
  const [suggested, setSuggested] = useState<Record<string, Record<string, unknown>>>({})
  const [suggesting, setSuggesting] = useState(false)
  const [proposed, setProposed] = useState(false)
  /** Whether the dictionary has answered at all. Without it, *loading* and
   *  *empty* are the same state, and the screen shows a spinner over a fact. */
  const [asked, setAsked] = useState(false)

  useEffect(() => {
    Promise.all([api.dictionary('measure'), api.dictionary('basis')])
      .then(([m, b]) => {
        setMeasures(m.map((e) => String(e.code)))
        setUnits(Object.fromEntries(m.map((e) => [String(e.code), String(e.unit ?? '')])))
        setBases(b.map((e) => String(e.code)))
      })
      .catch(() => {
        // A dictionary that cannot be read is a screen with empty pickers, and
        // the raw editor is the way through that.
        setRaw(true)
      })
      // Arrived is not the same as non-empty, and the screen has to be able to
      // tell them apart — see below.
      .finally(() => setAsked(true))
    // Apart, and not fatal: without it every limit offers only the whole fund,
    // and reading the workbook names the rows that still need holdings.
    api
      .migrationSubjects()
      .then((found) => {
        setSchemes(Array.isArray(found?.schemes) ? found.schemes : [])
        setNeedsSubject(Array.isArray(found?.needs_subject) ? found.needs_subject : [])
      })
      .catch(() => {})
  }, [])

  /** A share is of NAV; a count or a duration is of the portfolio itself. The first
   *  basis in the list was the default, and it is `benchmark`. */
  function basisFor(measure: string): string {
    const wanted = units[measure] === 'percent' ? 'nav' : 'portfolio'
    return bases.includes(wanted) ? wanted : (bases[0] ?? wanted)
  }

  function write(next: Record<string, Spec>) {
    onChange(next)
    setText(JSON.stringify(next, null, 2))
  }

  function choose(label: string, kind: string) {
    const next = { ...value }
    const current = value[label]
    const rating = RATING_KINDS.find((k) => k.value === kind)
    const fact = FACT_KINDS.find((k) => k.value === kind)
    if (!kind) {
      delete next[label]
    } else if (fact) {
      next[label] = { measure: '', basis: '', attribute: fact.attribute || slug(label) }
    } else if (rating) {
      next[label] = { measure: '', basis: '', ...rating.spec }
    } else {
      next[label] = {
        measure: kind,
        basis:
          current && !current.template && current.basis && units[current.measure] === units[kind]
            ? current.basis
            : basisFor(kind),
        // What the row covers belongs to the row, so a different measure keeps it.
        subject:
          current && !current.template ? (current.subject ?? WHOLE_FUND) : (suggested[label] ?? WHOLE_FUND),
      }
    }
    write(next)
  }

  function update(label: string, part: Partial<Spec>) {
    const current = value[label]
    if (current) write({ ...value, [label]: { ...current, ...part } })
  }

  const mapped = Object.keys(value).length
  const options = subjectOptions(schemes)
  const everyOption = options.flatMap((g) => g.options)

  // Proposed on arrival rather than on a click. The suggestions are a starting
  // point somebody corrects, and a starting point behind a button is one people
  // meet after they have already done the work by hand.
  //
  // Once only, and never over an answer: `proposed` stops a re-run from
  // arriving on top of choices somebody has since made.
  useEffect(() => {
    if (proposed || suggesting) return
    if (labels.length === 0 || bases.length === 0) return
    setProposed(true)
    void propose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels.join('|'), bases.length])

  async function propose() {
    setSuggesting(true)
    setError(null)
    try {
      const suggestions: MappingSuggestion[] = await api.suggestMapping(labels)
      const next = { ...value }
      const reasons: Record<string, string> = {}
      const covers: Record<string, Record<string, unknown>> = {}
      for (const s of suggestions) {
        reasons[s.label] = s.subject_why ? `${s.why}; ${s.subject_why}` : s.why
        if (s.subject && !s.template) covers[s.label] = s.subject
        // Never over-write a choice somebody has already made: a suggestion
        // that replaces an answer is not a suggestion.
        if (next[s.label]) continue
        if (s.attribute) {
          next[s.label] = { measure: '', basis: '', attribute: s.attribute }
        } else if (s.template) {
          next[s.label] = { measure: '', basis: '', template: s.template, subject: s.subject ?? WHOLE_FUND }
        } else if (s.measure) {
          next[s.label] = { measure: s.measure, basis: s.basis ?? basisFor(s.measure), subject: s.subject ?? WHOLE_FUND }
        } else if (s.subject) {
          // Read as text, and about the holdings its name picks out: Restricted Countries (HC).
          next[s.label] = { measure: '', basis: '', subject: s.subject }
        }
      }
      setWhy(reasons)
      setSuggested(covers)
      write(next)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSuggesting(false)
    }
  }

  return (
    <div className="card row-mapping">
      <h3>What each row means</h3>
      <p className="note">
        Match the rows that hold a number to the measure they count, and say which holdings each
        applies to. Rows holding a rating are a minimum rating, for every holding or for the fund on
        average. A benchmark or a base currency is a fact about the fund, recorded with it rather than
        as a limit. Rows you leave alone still import, as text. Nothing is dropped.
      </p>
      {!asked && !raw && <p className="loading">Loading terms…</p>}

      {/* An empty dictionary is a fact, not a wait. This said `Loading terms…`
          for as long as somebody left the screen open, because the spinner was
          keyed on the list being empty rather than on the answer not having
          arrived — so a fresh appliance, where nobody has seeded a market yet,
          looked exactly like a slow one. The suggestions never ran either
          (nothing to suggest *from*), which is why the count underneath read
          `0 of 36 rows mapped` and stayed there. */}
      {asked && !raw && measures.length === 0 && (
        <p className="warn" data-testid="no-vocabulary">
          No measures are defined yet, so there is nothing to match these rows
          against. Load the vocabulary for your market on{' '}
          <strong>Starter markets</strong>, under Dictionary, then come back.
          You can also go on without it: every row imports as text, marked as
          something your compliance system cannot check automatically, and
          nothing is dropped.
        </p>
      )}

      {labels.length > 0 && !raw && measures.length > 0 && (
        <div className="actions">
          <button className="ghost" disabled={suggesting} onClick={() => void propose()}>
            {suggesting ? 'Reading the rows…' : 'Suggest again'}
          </button>
          <span className="note">
            {suggesting
              ? 'Matching your rows against the terms you have defined…'
              : 'Suggestions are filled in below with the reason for each. Change any of them — nothing you choose is overwritten.'}
          </span>
        </div>
      )}

      {labels.length > 0 && !raw && measures.length > 0 && (
        <div className="table-scroll">
          <table className="grid" data-testid="row-mapping">
            <thead>
              <tr>
                <th>Row in your workbook</th>
                <th>What it is</th>
                <th>Against</th>
                <th>Applies to</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {labels.map((label) => {
                const spec = value[label]
                const measured = Boolean(spec && !spec.template && spec.measure)
                const key = subjectKey(spec?.subject)
                const known = everyOption.some((o) => o.key === key)
                const uncovered =
                  measured && key === subjectKey(WHOLE_FUND) && needsSubject.includes(spec!.measure)
                return (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>
                      <select
                        aria-label={`What “${label}” is`}
                        value={kindOf(spec)}
                        title="Leave blank to import this row as text."
                        onChange={(e) => choose(label, e.target.value)}
                      >
                        <option value="">— import as text —</option>
                        <optgroup label="A number">
                          {measures.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="A rating">
                          {RATING_KINDS.filter((k) => k.rating).map((k) => (
                            <option key={k.value} value={k.value}>
                              {k.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="How ratings are read">
                          {RATING_KINDS.filter((k) => !k.rating).map((k) => (
                            <option key={k.value} value={k.value}>
                              {k.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="A fact about the fund">
                          {FACT_KINDS.map((k) => (
                            <option key={k.value} value={k.value}>
                              {k.label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </td>
                    <td>
                      {measured && (
                        <select
                          aria-label={`What “${label}” is measured against`}
                          value={spec!.basis}
                          title="What the measure is a percentage or proportion of."
                          onChange={(e) => update(label, { basis: e.target.value })}
                        >
                          {bases.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {measured ? (
                        <>
                          <select
                            aria-label={`Which holdings “${label}” applies to`}
                            value={known ? key : ''}
                            onChange={(e) => {
                              const found = everyOption.find((o) => o.key === e.target.value)
                              if (found) update(label, { subject: found.subject })
                            }}
                          >
                            {!known && <option value="">{subjectText(spec!.subject)}</option>}
                            {options.map((g) =>
                              g.group ? (
                                <optgroup key={g.group} label={g.group}>
                                  {g.options.map((o) => (
                                    <option key={o.key} value={o.key}>
                                      {o.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ) : (
                                g.options.map((o) => (
                                  <option key={o.key} value={o.key}>
                                    {o.label}
                                  </option>
                                ))
                              ),
                            )}
                          </select>
                          {uncovered && (
                            <span className="uncovered" data-testid="uncovered-row">
                              A share of the whole fund is always all of it. Say which holdings, or
                              its cells import as text.
                            </span>
                          )}
                        </>
                      ) : spec?.attribute ? (
                        <span className="clause">Recorded with each fund, not a limit</span>
                      ) : spec?.template === 'rating_floor' ? (
                        <span className="clause">Every holding</span>
                      ) : spec?.template === 'average_rating_floor' ? (
                        <span className="clause">The fund, weighted by market value</span>
                      ) : spec?.template === 'evaluation_policy' ? (
                        <span className="clause">Every rating floor on the fund</span>
                      ) : spec?.subject ? (
                        <span className="clause">{subjectText(spec.subject)}</span>
                      ) : null}
                    </td>
                    <td className="clause">{why[label] ?? ''}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {labels.length === 0 && !raw && (
        <p className="empty">
          Upload a workbook, or paste one below, and its rows will be listed here.
        </p>
      )}

      <div className="actions">
        <span className="note">
          {mapped} of {labels.length || '—'} row(s) mapped.
        </span>
        <button className="ghost" onClick={() => setRaw(!raw)}>
          {raw ? 'Back to the list' : 'Paste a mapping instead'}
        </button>
      </div>

      {raw && (
        <>
          <label>
            Mapping
            <textarea
              rows={8}
              value={text}
              title="A row label, then the measure, basis and holdings it means. Useful when you already have one from another workbook of the same shape."
              onChange={(e) => {
                setText(e.target.value)
                try {
                  onChange(JSON.parse(e.target.value))
                  setError(null)
                } catch {
                  setError('That is not valid JSON yet.')
                }
              }}
            />
          </label>
          {error && <p className="error">{error}</p>}
        </>
      )}
    </div>
  )
}
